/**
 * Shareable/deep-linkable Flow chat URL helpers (round-4 item 7, 2026-08-20,
 * Spencer-approved query-param approach).
 *
 * Mirrors `lib/copyArtifactLink.ts`'s pattern — a pure URL builder (testable
 * without DOM mocks) plus a callback-based clipboard copy helper — but the
 * chat link layers `flowChat`/`flowChatAccount` query params onto WHATEVER
 * the current URL already is, rather than a fixed canonical route like
 * `/artifact/<id>`. The whole point of this feature is that a pasted link
 * reopens the app at that same location with the chat dock open on top of
 * it, not that it navigates somewhere new — see `useDockUrlSync.ts`'s
 * restore-on-load effect, the other consumer of these same two param names.
 *
 * Param names checked against every other query param already in use in
 * this codebase (`impersonate`, `contact`, `artifactId`, `kind`,
 * `deliveryStatus`, `handler`, `range`, `tab`, `status`, `producerKind`,
 * `concurrency`, `tags`, `expandSourceKind`, `ticket`, `redirect`, `reason`)
 * before picking these two — no collisions.
 *
 * Single source of truth for both the param NAMES and the URL CONSTRUCTION:
 * `useDockUrlSync.ts`'s read/write-to-URL effects and
 * `FlowChatPanel.tsx`'s "Copy chat link" button both import from here so the
 * three can never drift apart (the same rationale `copyArtifactLink.ts`'s
 * own doc comment gives for its two callsites).
 *
 * GTMOPS-2285 Track A: the param NAMES (and the nuqs parser group,
 * `flowChatParams`) now live in `lib/urlParams.ts` — the single file that
 * owns every other nuqs param group in the app — and are re-exported here so
 * every existing import of `FLOW_CHAT_URL_PARAM`/`FLOW_CHAT_ACCOUNT_URL_PARAM`
 * from this module keeps working unchanged. `urlParams.ts` is the source of
 * truth; this file no longer declares them.
 */

import { FLOW_CHAT_URL_PARAM, FLOW_CHAT_ACCOUNT_URL_PARAM } from '../../../lib/urlParams';

export { FLOW_CHAT_URL_PARAM, FLOW_CHAT_ACCOUNT_URL_PARAM };

export interface CopyFlowChatLinkCallbacks {
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}

/**
 * Build the shareable chat URL for `conversationId`/`accountId`, layered
 * onto `href` (defaults to `window.location.href`; tests pass it explicitly
 * so this stays DOM-mock-free). Any OTHER query params already on `href`
 * are preserved — only the two chat params are set/overwritten.
 */
export function flowChatLinkUrl(conversationId: string, accountId: string, href?: string): string {
  const base = href ?? (typeof window !== 'undefined' ? window.location.href : '');
  const url = new URL(base);
  url.searchParams.set(FLOW_CHAT_URL_PARAM, conversationId);
  url.searchParams.set(FLOW_CHAT_ACCOUNT_URL_PARAM, accountId);
  return url.toString();
}

/**
 * Copy the shareable chat URL to the clipboard, invoking onSuccess/onError
 * per the platform clipboard API result. Both callbacks are optional;
 * failures are swallowed when no onError is provided so callers that don't
 * care about feedback can use this safely — same contract as
 * `copyArtifactLink`.
 */
export async function copyFlowChatLink(
  conversationId: string,
  accountId: string,
  callbacks: CopyFlowChatLinkCallbacks = {},
): Promise<void> {
  try {
    await navigator.clipboard.writeText(flowChatLinkUrl(conversationId, accountId));
    callbacks.onSuccess?.();
  } catch (err) {
    callbacks.onError?.(err);
  }
}
